// Pipe Take-off — extraction progress. Polls /:id/status every 3s while
// the project is in a non-terminal state and renders per-stage progress
// dots. On completion redirects (or shows a Next button) to the detail
// page; on failure shows the error message + a Retry button.
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router";
import { api } from "../../../lib/api";

interface Status {
  projectId: number;
  status: string;
  errorMessage: string | null;
  liveStage: string | null;
  liveMessage: string | null;
  liveUpdatedAt: string | null;
}

const STAGES: Array<{ id: string; label: string }> = [
  { id: "preprocessing", label: "Document Intelligence" },
  { id: "extracting", label: "AI extraction" },
  { id: "validating", label: "Validation" },
  { id: "completed", label: "Complete" },
];

const TERMINAL = new Set(["completed", "failed"]);

export function ExtractionProgress() {
  const { id } = useParams<{ id: string }>();
  const projectId = Number(id);
  const [status, setStatus] = useState<Status | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!Number.isFinite(projectId)) {
      setError("Invalid project id");
      return;
    }
    let cancelled = false;
    let timer: number | undefined;

    const tick = async () => {
      try {
        const r = await api<Status>(
          `/api/tools/pipe-takeoff/projects/${projectId}/status`,
        );
        if (cancelled) return;
        setStatus(r);
        if (!TERMINAL.has(r.status)) {
          timer = window.setTimeout(() => void tick(), 3_000);
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      }
    };
    void tick();
    return () => {
      cancelled = true;
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [projectId]);

  const stageReached = (stageId: string): "done" | "active" | "pending" => {
    if (!status) return "pending";
    if (status.status === "failed") return "pending";
    if (status.status === "completed") return "done";
    if (status.status === stageId) return "active";
    const order = STAGES.map((s) => s.id);
    const currentIdx = order.indexOf(status.status);
    const stageIdx = order.indexOf(stageId);
    return stageIdx < currentIdx ? "done" : "pending";
  };

  return (
    <>
      <div className="bsp-page-header">
        <h1 className="bsp-page-title">Extracting</h1>
      </div>

      {error && (
        <div className="bsp-card" style={{ borderColor: "var(--bsp-red)" }}>
          <p style={{ color: "var(--bsp-red-fg)", margin: 0 }}>{error}</p>
        </div>
      )}

      {!status && !error && <p>Loading status…</p>}

      {status && (
        <div className="bsp-card" style={{ display: "grid", gap: 16 }}>
          <ol style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gap: 10 }}>
            {STAGES.map((s) => {
              const reached = stageReached(s.id);
              const colour =
                reached === "done"
                  ? "var(--bsp-green)"
                  : reached === "active"
                    ? "var(--bsp-blue)"
                    : "var(--bsp-fg-subtle)";
              return (
                <li
                  key={s.id}
                  style={{ display: "flex", alignItems: "center", gap: 12 }}
                >
                  <span
                    style={{
                      width: 14,
                      height: 14,
                      borderRadius: 999,
                      background: colour,
                      flex: "0 0 14px",
                    }}
                  />
                  <span style={{ fontWeight: reached === "active" ? 700 : 500 }}>
                    {s.label}
                  </span>
                  {reached === "active" && (
                    <span
                      className="bsp-mono"
                      style={{ fontSize: 12, color: "var(--bsp-fg-muted)" }}
                    >
                      in progress…
                    </span>
                  )}
                </li>
              );
            })}
          </ol>

          {status.status === "completed" && (
            <Link
              to={`/tools/pipe-takeoff/${projectId}`}
              className="bsp-btn bsp-btn-primary"
              style={{ justifySelf: "start" }}
            >
              Open results
            </Link>
          )}

          {status.status === "failed" && (
            <>
              <p style={{ color: "var(--bsp-red-fg)", margin: 0 }}>
                Extraction failed: {status.errorMessage ?? "(no message)"}
              </p>
              <button
                type="button"
                className="bsp-btn bsp-btn-secondary"
                style={{ justifySelf: "start" }}
                onClick={async () => {
                  try {
                    await api(
                      `/api/tools/pipe-takeoff/projects/${projectId}/extract`,
                      { method: "POST" },
                    );
                    window.location.reload();
                  } catch (e) {
                    setError(e instanceof Error ? e.message : String(e));
                  }
                }}
              >
                Retry
              </button>
            </>
          )}
        </div>
      )}
    </>
  );
}
